import { useMutation } from '@apollo/client';
import { GENERATE_COMMUNITY_PROMPTS, GET_COMMUNITY_PROMPTS } from '@communities/prompt-management/graphql';
import { transformErrors } from '@core/adapters/errors/transformError';
import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import { useState, useCallback } from 'react';

export interface GenerateCommunityPromptsInput {
  labelFeedback: Array<{
    labelName: string;
    falsePositives?: string[];
    falseNegatives?: string[];
  }>;
}

interface PromptDetails {
  fileName: string;
  promptVersion: string;
  isNew?: boolean;
}

interface GenerateCommunityPromptsResponse {
  generateCommunityPrompts: {
    version: string;
    classificationFileName: string;
    moderationFileName: string;
    classification: PromptDetails;
    moderation: PromptDetails;
  };
}

export function useGenerateCommunityPrompt() {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const [generatePromptsMutation, { loading }] = useMutation<GenerateCommunityPromptsResponse>(GENERATE_COMMUNITY_PROMPTS);

  const resetErrors = useCallback(() => {
    setErrors(null);
  }, []);

  // eslint-disable-next-line complexity
  const generatePrompts = async (
    communityId: string,
    input: GenerateCommunityPromptsInput
  ): Promise<{ success: boolean; version?: string; isNew?: boolean }> => {
    try {
      track('GenerateCommunityPromptInitiated', {
        communityId,
      });

      const result = await generatePromptsMutation({
        variables: {
          communityId,
          input: {
            labelFeedback: input.labelFeedback,
          },
        },
        refetchQueries: communityId ? [{ query: GET_COMMUNITY_PROMPTS, variables: { communityId } }] : [],
        awaitRefetchQueries: true,
      });

      const version = result.data?.generateCommunityPrompts?.version;
      const isNew = result.data?.generateCommunityPrompts?.classification?.isNew;

      track('GenerateCommunityPromptSucceeded', {
        communityId,
        promptVersion: version || 'unknown',
        isNew: isNew ? 'true' : 'false',
      });

      return {
        success: true,
        version,
        isNew,
      };
    } catch (error) {
      track('GenerateCommunityPromptFailed', {
        communityId,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });

      setErrors(transformErrors(error));

      return {
        success: false,
      };
    }
  };

  return {
    generatePrompts,
    loading,
    errors,
    resetErrors,
  };
}

export default useGenerateCommunityPrompt;
